<template>
  <van-popup v-model="visible" :close-on-click-overlay="false" position="bottom" :style="{ height: '100%' }">
    <div class="crop-container">
      <!-- 顶部操作栏 -->
      <div class="crop-header">
        <div class="header-btn" @click="handleCancel">{{ $t('cancel') }}</div>
        <div class="header-btn confirm" @click="handleConfirm">{{ $t('confirm') }}</div>
      </div>

      <!-- 图片区域 -->
      <div class="crop-area" ref="cropArea">
        <div class="image-wrapper" :style="imageWrapperStyle">
          <img ref="cropImage" :src="imageSrc" @load="onImageLoad" crossorigin="anonymous" draggable="false" />
        </div>
        <!-- 裁剪框遮罩 -->
        <div class="crop-overlay">
          <div class="crop-circle" :style="cropCircleStyle"></div>
        </div>
      </div>

      <!-- 底部提示 -->
      <div class="crop-footer">
        {{ $t('crop_hint') }}
      </div>
    </div>
  </van-popup>
</template>

<script>
import { Popup, Toast } from 'vant'

export default {
  name: 'AvatarCropDialog',
  components: {
    'van-popup': Popup
  },
  props: {
    imageFile: {
      type: File,
      default: null
    }
  },
  data() {
    return {
      visible: false,
      imageSrc: '',
      imageWidth: 0,
      imageHeight: 0,
      scale: 1,
      translateX: 0,
      translateY: 0,
      // 触摸状态
      isDragging: false,
      lastX: 0,
      lastY: 0,
      lastDistance: 0,
      lastScale: 1,
      // 图片在容器中的实际显示尺寸
      displayWidth: 0,
      displayHeight: 0,
      // 裁剪框实际尺寸
      cropSize: 0
    }
  },
  computed: {
    imageWrapperStyle() {
      return {
        transform: `translate(${this.translateX}px, ${this.translateY}px) scale(${this.scale})`,
        transformOrigin: 'center center'
      }
    },
    cropCircleStyle() {
      return {
        width: this.cropSize + 'px',
        height: this.cropSize + 'px'
      }
    }
  },
  watch: {
    imageFile: {
      immediate: true,
      handler(file) {
        if (file) {
          this.loadImage(file)
        }
      }
    }
  },
  methods: {
    show() {
      this.visible = true
      this.resetState()
      this.$nextTick(() => {
        this.bindEvents()
      })
    },
    hide() {
      this.visible = false
      this.unbindEvents()
    },
    resetState() {
      this.scale = 1
      this.translateX = 0
      this.translateY = 0
      this.lastScale = 1
    },
    loadImage(file) {
      const reader = new FileReader()
      reader.onload = (e) => {
        this.imageSrc = e.target.result
      }
      reader.readAsDataURL(file)
    },
    onImageLoad() {
      const img = this.$refs.cropImage
      this.imageWidth = img.naturalWidth
      this.imageHeight = img.naturalHeight

      this.$nextTick(() => {
        this.calculateDisplaySize()
      })
    },
    calculateDisplaySize() {
      const cropArea = this.$refs.cropArea
      const img = this.$refs.cropImage
      if (!cropArea || !img) return

      const areaWidth = cropArea.clientWidth
      const areaHeight = cropArea.clientHeight

      // 计算图片在 contain 模式下的实际显示尺寸
      const fitScale = Math.min(areaWidth / this.imageWidth, areaHeight / this.imageHeight)
      this.displayWidth = this.imageWidth * fitScale
      this.displayHeight = this.imageHeight * fitScale

      // 裁剪框尺寸（屏幕最小尺寸的 70%）
      this.cropSize = Math.min(areaWidth, areaHeight) * 0.7

      console.log('Image size:', this.imageWidth, 'x', this.imageHeight)
      console.log('Display size:', this.displayWidth, 'x', this.displayHeight)
      console.log('Crop size:', this.cropSize)
    },
    // 触摸事件处理 - 支持单指拖动和双指缩放
    onTouchStart(e) {
      try {
        e.preventDefault()
      } catch (err) {}
      
      if (e.touches.length === 1) {
        this.isDragging = true
        this.lastX = e.touches[0].clientX
        this.lastY = e.touches[0].clientY
      } else if (e.touches.length === 2) {
        this.isDragging = false
        this.lastDistance = this.getDistance(e.touches[0], e.touches[1])
        this.lastScale = this.scale
        // 记录双指中心位置用于后续平移
        this.lastCenterX = (e.touches[0].clientX + e.touches[1].clientX) / 2
        this.lastCenterY = (e.touches[0].clientY + e.touches[1].clientY) / 2
      }
    },
    onTouchMove(e) {
      try {
        e.preventDefault()
      } catch (err) {}
      
      if (e.touches.length === 1 && this.isDragging) {
        // 单指拖动
        const dx = e.touches[0].clientX - this.lastX
        const dy = e.touches[0].clientY - this.lastY
        this.translateX += dx
        this.translateY += dy
        this.lastX = e.touches[0].clientX
        this.lastY = e.touches[0].clientY
      } else if (e.touches.length === 2) {
        // 双指缩放
        const distance = this.getDistance(e.touches[0], e.touches[1])
        const newScale = this.lastScale * (distance / this.lastDistance)
        this.scale = Math.max(0.1, Math.min(10, newScale))
        
        // 双指平移
        const centerX = (e.touches[0].clientX + e.touches[1].clientX) / 2
        const centerY = (e.touches[0].clientY + e.touches[1].clientY) / 2
        const dx = centerX - this.lastCenterX
        const dy = centerY - this.lastCenterY
        this.translateX += dx
        this.translateY += dy
        this.lastCenterX = centerX
        this.lastCenterY = centerY
      }
    },
    onTouchEnd(e) {
      e.preventDefault()
      this.isDragging = false
    },
    // 鼠标事件处理
    onMouseDown(e) {
      // 忽略右键
      if (e.button !== 0) return
      this.isDragging = true
      this.lastX = e.clientX
      this.lastY = e.clientY
    },
    onMouseMove(e) {
      if (!this.isDragging) return
      const dx = e.clientX - this.lastX
      const dy = e.clientY - this.lastY
      this.translateX += dx
      this.translateY += dy
      this.lastX = e.clientX
      this.lastY = e.clientY
    },
    onMouseUp() {
      this.isDragging = false
    },
    // 滚轮缩放（支持 Mac touchpad 双指缩放）
    onWheel(e) {
      try {
        e.preventDefault()
      } catch (err) {}
      
      // Mac touchpad 双指缩放时会有 ctrlKey
      if (e.ctrlKey) {
        const delta = e.deltaY > 0 ? 0.95 : 1.05
        this.scale = Math.max(0.1, Math.min(10, this.scale * delta))
      } else {
        // 普通滚轮 - 用于缩放
        const delta = e.deltaY > 0 ? 0.9 : 1.1
        this.scale = Math.max(0.1, Math.min(10, this.scale * delta))
      }
    },
    getDistance(touch1, touch2) {
      const dx = touch1.clientX - touch2.clientX
      const dy = touch1.clientY - touch2.clientY
      return Math.sqrt(dx * dx + dy * dy)
    },
    handleCancel() {
      this.hide()
      this.$emit('cancel')
    },
    async handleConfirm() {
      try {
        const croppedBlob = await this.cropImage()
        if (croppedBlob) {
          this.$emit('confirm', croppedBlob)
          this.hide()
        }
      } catch (error) {
        console.error('Crop error:', error)
        Toast(this.$t('toast_crop_failed'))
      }
    },
    async cropImage() {
      const cropArea = this.$refs.cropArea
      const img = this.$refs.cropImage
      if (!cropArea || !img) return null

      // 获取裁剪区域在屏幕上的实际位置
      const cropAreaRect = cropArea.getBoundingClientRect()
      
      // 裁剪框在屏幕上的位置（居中）
      const cropScreenX = cropAreaRect.left + cropAreaRect.width / 2
      const cropScreenY = cropAreaRect.top + cropAreaRect.height / 2
      const cropRadius = (Math.min(cropAreaRect.width, cropAreaRect.height) * 0.7) / 2

      // 获取图片元素在屏幕上的实际位置和尺寸
      const imgRect = img.getBoundingClientRect()
      
      // 图片的显示尺寸
      const displayedWidth = imgRect.width
      const displayedHeight = imgRect.height
      
      // 图片左上角在屏幕上的坐标
      const imgScreenX = imgRect.left
      const imgScreenY = imgRect.top

      console.log('Crop debug:', {
        cropScreen: cropScreenX + ',' + cropScreenY,
        cropRadius: cropRadius,
        imgScreen: imgScreenX + ',' + imgScreenY,
        imgDisplay: displayedWidth + 'x' + displayedHeight,
        imgNatural: this.imageWidth + 'x' + this.imageHeight
      })

      // 计算裁剪框相对于图片的位置
      const relX = cropScreenX - imgScreenX
      const relY = cropScreenY - imgScreenY

      // 转换为原始图片坐标
      const scaleX = this.imageWidth / displayedWidth
      const scaleY = this.imageHeight / displayedHeight

      const srcCenterX = relX * scaleX
      const srcCenterY = relY * scaleY
      const srcRadius = cropRadius * scaleX

      // 正方形裁剪区域
      let srcX = srcCenterX - srcRadius
      let srcY = srcCenterY - srcRadius
      const srcSize = srcRadius * 2

      // 边界检查
      srcX = Math.max(0, srcX)
      srcY = Math.max(0, srcY)
      let actualSrcSize = Math.min(srcSize, this.imageWidth - srcX, this.imageHeight - srcY)
      actualSrcSize = Math.max(1, actualSrcSize)

      console.log('Crop src:', { srcX, srcY, srcSize: actualSrcSize })

      // 使用 canvas 裁剪
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      const maxSize = 1024 // 输出尺寸，与后端 l 尺寸对齐

      canvas.width = maxSize
      canvas.height = maxSize

      // 绘制裁剪区域
      ctx.drawImage(
        img,
        srcX, srcY, actualSrcSize, actualSrcSize,
        0, 0, maxSize, maxSize
      )

      // 转换为 Blob
      return new Promise((resolve) => {
        canvas.toBlob((blob) => {
          resolve(blob)
        }, 'image/jpeg', 0.85)
      })
    },
    // 阻止浏览器默认缩放
    preventBrowserZoom(e) {
      e.preventDefault()
    },
    // 设置 body 的样式来防止缩放（简化版）
    setBodyZoomLock(locked) {
      if (locked) {
        // Safari 需要 manipulation 而不是 none
        document.body.style.touchAction = 'manipulation'
      } else {
        document.body.style.touchAction = ''
      }
    },
    bindEvents() {
      const cropArea = this.$refs.cropArea
      if (cropArea) {
        // 触摸事件
        cropArea.addEventListener('touchstart', this.onTouchStart, { passive: false })
        cropArea.addEventListener('touchmove', this.onTouchMove, { passive: false })
        cropArea.addEventListener('touchend', this.onTouchEnd)
        // 鼠标事件
        cropArea.addEventListener('mousedown', this.onMouseDown)
        cropArea.addEventListener('wheel', this.onWheel, { passive: false })
      }
      // 在 window 上监听 mouseup 和 mousemove，确保拖拽不会断开
      window.addEventListener('mouseup', this.onMouseUp)
      window.addEventListener('mousemove', this.onMouseMove)
      // 监听窗口大小变化
      window.addEventListener('resize', this.calculateDisplaySize)
      // 锁定 body 的缩放
      this.setBodyZoomLock(true)
    },
    unbindEvents() {
      const cropArea = this.$refs.cropArea
      if (cropArea) {
        cropArea.removeEventListener('touchstart', this.onTouchStart)
        cropArea.removeEventListener('touchmove', this.onTouchMove)
        cropArea.removeEventListener('touchend', this.onTouchEnd)
        cropArea.removeEventListener('mousedown', this.onMouseDown)
        cropArea.removeEventListener('wheel', this.onWheel)
      }
      window.removeEventListener('mouseup', this.onMouseUp)
      window.removeEventListener('mousemove', this.onMouseMove)
      window.removeEventListener('resize', this.calculateDisplaySize)
      // 解锁 body 的缩放
      this.setBodyZoomLock(false)
    }
  },
  mounted() {
    // 如果可见就立即绑定事件，否则等待 show()
    if (this.visible) {
      this.$nextTick(() => {
        this.bindEvents()
      })
    }
  },
  beforeDestroy() {
    this.unbindEvents()
  }
}
</script>

<style scoped>
.crop-container {
  width: 100%;
  height: 100%;
  background: #000;
  display: flex;
  flex-direction: column;
  touch-action: manipulation;  /* Safari 需要这个而不是 none */
  overscroll-behavior: none;
}

.crop-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 56px;
  padding: 0 16px;
  background: rgba(0, 0, 0, 0.8);
  position: relative;
  z-index: 10;
}

.header-btn {
  padding: 8px 16px;
  color: #fff;
  font-size: 16px;
  cursor: pointer;
  font-weight: 500;
}

.header-btn.confirm {
  color: #E8543A;
  font-weight: 600;
}

.crop-area {
  flex: 1;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  touch-action: manipulation;  /* Safari 支持 */
  cursor: grab;
  user-select: none;
  -webkit-user-select: none;
}

.crop-area:active {
  cursor: grabbing;
}

.image-wrapper {
  will-change: transform;
}

.image-wrapper img {
  display: block;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  pointer-events: none;
  touch-action: manipulation;  /* Safari 支持 */
}

.crop-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
}

.crop-circle {
  aspect-ratio: 1;
  border: 2px solid #fff;
  border-radius: 16%;
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.7);
}

.crop-footer {
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.6);
  font-size: 13px;
}
</style>



// WEBPACK FOOTER //
// src/components/AvatarCropDialog.vue